import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { InviteCode } from "@common/types/inviteCode";
import { hasAtLeastOrganizationRole } from "./lib";
import {
    validateCancelInvite,
    validateCreateClient,
    validateManageOrganizationSettings,
    validateResendInvite,
    validateUpdateTeamMember,
} from "./organizationRequestValidators";

export function canManageOrganizationSettings(
    actingUserRole: OrganizationUserRole,
) {
    return !validateManageOrganizationSettings(actingUserRole);
}

export function canViewOrganizationBilling(
    actingUserRole: OrganizationUserRole,
) {
    return hasAtLeastOrganizationRole(
        actingUserRole,
        OrganizationUserRole.ADMIN,
    );
}

export function canManageOrganizationBilling(
    actingUserRole: OrganizationUserRole,
) {
    return actingUserRole === OrganizationUserRole.OWNER;
}

export function canIssueBillingTransferRequest(
    actingUserRole: OrganizationUserRole,
) {
    return actingUserRole === OrganizationUserRole.OWNER;
}

export function canCreateClient(actingUserRole: OrganizationUserRole) {
    return !validateCreateClient(actingUserRole);
}

export function canInviteTeamMember(actingUserRole: OrganizationUserRole) {
    return hasAtLeastOrganizationRole(
        actingUserRole,
        OrganizationUserRole.MANAGER,
    );
}

export function canCancelInvite(
    actingUserRole: OrganizationUserRole,
    actingUserWorkspaceAssignments: string[],
    invite: InviteCode,
) {
    if (!invite.organizationRole) {
        return false;
    }

    if (!invite.workspaceAssignments) {
        return false;
    }

    return !validateCancelInvite({
        actingUser: {
            role: actingUserRole,
            workspaceAssignments: actingUserWorkspaceAssignments,
        },
        invite: {
            organizationRole: invite.organizationRole,
            workspaceAssignments: invite.workspaceAssignments.map(
                (assignment) => assignment.workspace.id,
            ),
        },
    });
}

export function canResendInvite(actingUserRole: OrganizationUserRole) {
    return !validateResendInvite(actingUserRole);
}

interface CanManageTeamMemberParams {
    actingUser: {
        id: number;
        role: OrganizationUserRole;
        workspaceAssignments: string[];
    };
    managedUser: {
        id: number;
        role: OrganizationUserRole;
        workspaceAssignments: string[];
    };
}

export function canManageTeamMember({
    actingUser,
    managedUser,
}: CanManageTeamMemberParams) {
    return !validateUpdateTeamMember({
        actingUser,
        managedUser,
    });
}

interface CanMutateCloseTemplateParams {
    actingUserId: number;
    actingUserRole: OrganizationUserRole;
    templateCreatedByUserId: number | null | undefined;
    /**
     * Org role of the template's creator, resolved by the caller. Only
     * consulted for the Manager branch acting on a template they did not
     * create. Pass `null` when the creator is no longer an org member or
     * their role is unknown (treated as a deny).
     */
    templateCreatorRole: OrganizationUserRole | null | undefined;
}

/**
 * Pure predicate deciding whether a user may mutate (edit / delete / archive)
 * a close template based on role-based ownership rules:
 * - Owner / Admin: any template
 * - Manager: templates they created OR created by a Member
 * - Member: only templates they created
 * - Unknown creator: deny for non-admins
 *
 * Shared between the backend guard and the frontend permission hook so the
 * two never drift.
 */
export function canMutateCloseTemplate({
    actingUserId,
    actingUserRole,
    templateCreatedByUserId,
    templateCreatorRole,
}: CanMutateCloseTemplateParams): boolean {
    const isOwnerOrAdmin = hasAtLeastOrganizationRole(
        actingUserRole,
        OrganizationUserRole.ADMIN,
    );
    if (isOwnerOrAdmin) {
        return true;
    }

    if (templateCreatedByUserId == null) {
        return false;
    }

    const isOwnTemplate = templateCreatedByUserId === actingUserId;

    if (actingUserRole === OrganizationUserRole.MANAGER) {
        if (isOwnTemplate) {
            return true;
        }
        return templateCreatorRole === OrganizationUserRole.MEMBER;
    }

    return isOwnTemplate;
}
